// ============================================
// PARAMÈTRES — LOGIQUE DE L'ÉCRAN
// ============================================
// Recherche, regroupement, bornes, dépendances manquantes : tout ce qui se
// décide sans React, testé à part.

import type {
  CategorieParametres, DependanceParametre, EtatDependances, ParametreDetaille,
} from '../../services/api';

/** Ce qu'il manque, dit pour un administrateur non technicien : `court` pour
 *  une mention brève, `explication` pour le panneau « À compléter » d'une
 *  rubrique, `manque` (explication précédée de « Sans effet ») pour une
 *  phrase isolée. */
const EXPLICATIONS: Record<DependanceParametre, { court: string; explication: string }> = {
  // Nom historique : veut dire « aucun moyen d'envoi » (ni SMTP, ni boîte
  // d'envoi Microsoft 365), voir le catalogue du serveur.
  smtp: {
    court: 'aucun moyen d’envoi',
    explication: "Aucun moyen d'envoi d'e-mails (ni SMTP sur le serveur, ni boîte d'envoi Microsoft 365). Choisissez une boîte d'envoi dans « Boîtes mail », ou demandez le SMTP à l'équipe technique.",
  },
  cleIa: {
    court: 'clé IA absente',
    explication: "Aucune clé d'API n'est enregistrée pour le fournisseur choisi. Collez la clé OpenAI (sk-…) ou Gemini (AIza…) dans « Clé API du fournisseur ».",
  },
  prestashop: {
    court: 'accès PrestaShop absent',
    explication: "Le serveur n'a pas d'accès à la base de la boutique klygroupe.com. À demander à l'équipe technique.",
  },
  telephonie: {
    court: 'téléphonie non configurée',
    explication: "La clé d'API des outils et le secret du webhook de fin d'appel doivent être renseignés tous les deux (rubrique Téléphonie).",
  },
  connecteurMail: {
    court: 'aucune boîte active',
    explication: "Aucune boîte mail n'est activée. Ajoutez ou activez une boîte dans « Boîtes mail ».",
  },
};

export const DEPENDANCES: Record<DependanceParametre, { court: string; explication: string; manque: string }> =
  Object.fromEntries(
    (Object.keys(EXPLICATIONS) as DependanceParametre[]).map(d => [d, {
      ...EXPLICATIONS[d],
      manque: `Sans effet pour l'instant : ${EXPLICATIONS[d].explication.charAt(0).toLowerCase()}${EXPLICATIONS[d].explication.slice(1)}`,
    }]),
  ) as Record<DependanceParametre, { court: string; explication: string; manque: string }>;

// --- Intelligence artificielle ---------------------------------------------

/** Modèle utilisé quand « Modèle » est laissé vide (même table que le serveur,
 *  services/ai/fournisseur.ts). */
export const MODELES_IA_PAR_DEFAUT: Record<string, string> = {
  gemini: 'gemini-2.5-flash',
  openai: 'gpt-5',
};

/** Modèles proposés dans la liste « Modèle », par fournisseur. La première
 *  entrée a la valeur '' : c'est le modèle par défaut du fournisseur
 *  (MODELES_IA_PAR_DEFAUT), enregistré vide pour suivre ce défaut. */
export const MODELES_IA: Record<string, ReadonlyArray<{ valeur: string; libelle: string }>> = {
  openai: [
    { valeur: '', libelle: 'GPT-5 : meilleure qualité (recommandé)' },
    { valeur: 'gpt-5-mini', libelle: 'GPT-5 mini : plus rapide, moins cher' },
    { valeur: 'gpt-5-nano', libelle: 'GPT-5 nano : très rapide, tâches simples' },
  ],
  gemini: [
    { valeur: '', libelle: 'Gemini 2.5 Flash : rapide (recommandé)' },
    { valeur: 'gemini-2.5-pro', libelle: 'Gemini 2.5 Pro : plus puissant, plus lent' },
  ],
};

/** Valeur de la liste qui ouvre la saisie libre d'un autre modèle. */
export const MODELE_AUTRE = '__autre__';

/**
 * Entrée de la liste « Modèle » qui correspond à la valeur enregistrée :
 * '' pour le défaut du fournisseur (vide, ou nom identique au défaut), le nom
 * s'il est proposé, MODELE_AUTRE sinon (modèle saisi à la main).
 */
export function choixModele(valeur: string | null | undefined, fournisseur: string): string {
  const v = (valeur ?? '').trim();
  if (!v || v === MODELES_IA_PAR_DEFAUT[fournisseur]) return '';
  return (MODELES_IA[fournisseur] ?? []).some(m => m.valeur === v) ? v : MODELE_AUTRE;
}

const LIBELLES_OPTIONS: Record<string, Record<string, string>> = {
  'ia.fournisseur': { gemini: 'Google Gemini', openai: 'OpenAI (GPT-5)' },
};

/** Libellé lisible d'une option de liste ; la valeur brute à défaut. */
export function libelleOption(cle: string, option: string): string {
  return LIBELLES_OPTIONS[cle]?.[option] ?? option;
}

/** Texte d'attente d'un champ texte vide. Pour le modèle d'IA, il dit quel
 *  modèle sera utilisé avec le fournisseur choisi. */
export function texteAttente(
  p: Pick<ParametreDetaille, 'cle'>,
  parametres: ReadonlyArray<Pick<ParametreDetaille, 'cle' | 'valeur'>>,
): string {
  if (p.cle === 'ia.modele') {
    const fournisseur = parametres.find(x => x.cle === 'ia.fournisseur')?.valeur || 'gemini';
    const modele = MODELES_IA_PAR_DEFAUT[fournisseur];
    return modele ? `Par défaut : ${modele}` : 'Modèle par défaut du fournisseur';
  }
  if (p.cle === 'ia.tarifs') return 'Non renseigné : {"gpt-5":{"entree":…,"sortie":…}}';
  return 'Non renseigné';
}

/** Texte d'attente d'une clé secrète : rappelle quelle clé coller pour l'IA. */
export function texteAttenteSecret(
  p: Pick<ParametreDetaille, 'cle' | 'renseigne'>,
  parametres: ReadonlyArray<Pick<ParametreDetaille, 'cle' | 'valeur'>>,
): string {
  if (p.renseigne) return '•••••••• (configurée)';
  if (p.cle === 'ia.cle_api') {
    const fournisseur = parametres.find(x => x.cle === 'ia.fournisseur')?.valeur;
    return fournisseur === 'openai' ? 'Collez la clé OpenAI (sk-…)' : 'Collez la clé Gemini (AIza…)';
  }
  return 'Non configurée';
}

/** Après quel enregistrement relire l'état des dépendances : une clé saisie
 *  peut lever un avertissement, et la clé IA dépend du fournisseur choisi. */
export function doitRelireDependances(p: Pick<ParametreDetaille, 'cle' | 'type'>): boolean {
  return p.type === 'SECRET' || p.cle === 'ia.fournisseur';
}

/** Minuscules sans accents, pour une recherche tolérante. */
export function normaliser(texte: string): string {
  return texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/** Garde les paramètres dont le libellé, la description, la clé ou la
 *  catégorie contiennent tous les mots cherchés. */
export function filtrerParametres<T extends ParametreDetaille>(liste: T[], recherche: string): T[] {
  const mots = normaliser(recherche).split(/\s+/).filter(Boolean);
  if (mots.length === 0) return liste;
  return liste.filter(p => {
    const texte = normaliser(`${p.libelle} ${p.description} ${p.cle} ${p.categorie}`);
    return mots.every(m => texte.includes(m));
  });
}

export interface GroupeParametres<T> {
  nom: string;
  description: string | null;
  parametres: T[];
}

/** Regroupe par catégorie, dans l'ordre donné par le serveur ; une catégorie
 *  inconnue de cet ordre vient à la suite, sans description. */
export function grouperParCategorie<T extends ParametreDetaille>(
  liste: T[],
  categories: CategorieParametres[] = [],
): GroupeParametres<T>[] {
  const groupes = new Map<string, GroupeParametres<T>>();
  for (const c of categories) groupes.set(c.nom, { nom: c.nom, description: c.description, parametres: [] });
  for (const p of liste) {
    if (!groupes.has(p.categorie)) groupes.set(p.categorie, { nom: p.categorie, description: null, parametres: [] });
    groupes.get(p.categorie)!.parametres.push(p);
  }
  return [...groupes.values()].filter(g => g.parametres.length > 0);
}

/** « Entre 1 et 1440, nombre entier ». Null si aucune borne. */
export function texteBornes(p: Pick<ParametreDetaille, 'min' | 'max' | 'entier'>): string | null {
  let t: string | null = null;
  if (p.min !== undefined && p.max !== undefined) t = `Entre ${p.min} et ${p.max}`;
  else if (p.min !== undefined) t = `Au moins ${p.min}`;
  else if (p.max !== undefined) t = `Au plus ${p.max}`;
  if (p.entier) t = t ? `${t}, nombre entier` : 'Nombre entier';
  return t;
}

/** Même contrôle que le serveur, pour prévenir avant l'envoi. Null si valide. */
export function validerNombre(p: Pick<ParametreDetaille, 'min' | 'max' | 'entier'>, saisie: string): string | null {
  const brut = saisie.trim().replace(',', '.');
  const n = brut === '' ? NaN : Number(brut);
  if (!Number.isFinite(n)) return 'Saisissez un nombre.';
  if (p.entier && !Number.isInteger(n)) return 'Saisissez un nombre entier.';
  if ((p.min !== undefined && n < p.min) || (p.max !== undefined && n > p.max)) {
    return `Valeur hors limites : ${texteBornes({ min: p.min, max: p.max })?.toLowerCase()}.`;
  }
  return null;
}

/** Valeur à envoyer pour un nombre saisi (virgule acceptée). */
export function nombreAEnvoyer(saisie: string): string {
  return saisie.trim().replace(',', '.');
}

/** Dépendances déclarées par le paramètre et absentes. Rien tant que l'état
 *  n'est pas connu : on n'affiche pas d'avertissement sur une supposition. */
export function dependancesManquantes(
  p: Pick<ParametreDetaille, 'dependances'>,
  etat: EtatDependances | null | undefined,
): DependanceParametre[] {
  if (!etat) return [];
  return (p.dependances ?? []).filter(d => etat[d] === false);
}

/** Valeur par défaut dite en clair. */
export function libelleValeurParDefaut(p: Pick<ParametreDetaille, 'type' | 'valeurParDefaut'>): string {
  const v = p.valeurParDefaut;
  if (p.type === 'BOOLEEN') return v === 'true' ? 'activé' : 'désactivé';
  if (v === null || v === undefined || v === '') return 'vide';
  return v;
}

/** Message d'erreur lisible pour un enregistrement refusé. */
export function messageErreur(e: unknown): string {
  const statut = (e as { statusCode?: unknown } | null)?.statusCode;
  const code = String((e as { code?: unknown } | null)?.code ?? '');
  // Un 403 « mot de passe à changer » garde son propre message.
  if (statut === 403 && !/MOT_DE_PASSE/i.test(code)) return 'Seul un administrateur peut modifier les paramètres.';
  if (statut === 401) return 'Votre session a expiré : reconnectez-vous puis recommencez.';
  if (e instanceof Error && e.message) return e.message;
  return 'Enregistrement impossible. Réessayez dans un instant.';
}

// --- Voyants et réglages sans effet ------------------------------------------

/** Interrupteurs généraux : coupés, ils rendent sans effet les autres
 *  réglages dont la clé commence par le préfixe associé. */
export const INTERRUPTEURS_GENERAUX: Record<string, string> = {
  'ia.actif': 'ia.',
  'email.ingestion.actif': 'email.',
  'telephonie.actif': 'telephonie.',
};

type Lisible = Pick<ParametreDetaille, 'cle' | 'type' | 'valeur' | 'libelle' | 'dependances'>;

/** L'interrupteur général qui gouverne ce réglage, s'il en a un (jamais lui-même). */
export function interrupteurGeneral<T extends Lisible>(p: Pick<ParametreDetaille, 'cle'>, parametres: ReadonlyArray<T>): T | null {
  for (const [cle, prefixe] of Object.entries(INTERRUPTEURS_GENERAUX)) {
    if (p.cle !== cle && p.cle.startsWith(prefixe)) return parametres.find(x => x.cle === cle) ?? null;
  }
  return null;
}

/** Vrai si l'interrupteur général de ce réglage est coupé. */
export function estNeutralise(p: Pick<ParametreDetaille, 'cle'>, parametres: ReadonlyArray<Lisible>): boolean {
  const maitre = interrupteurGeneral(p, parametres);
  return maitre !== null && maitre.valeur !== 'true';
}

/** Une seule phrase, et seulement pour un interrupteur allumé qui ne produit
 *  rien : c'est le seul cas où l'écran affirme quelque chose de faux sans
 *  elle. Null sinon. */
export function texteSansEffet(
  p: Lisible,
  parametres: ReadonlyArray<Lisible>,
  etat: EtatDependances | null | undefined,
): string | null {
  if (p.type !== 'BOOLEEN' || p.valeur !== 'true') return null;
  const raisons: string[] = [];
  const maitre = interrupteurGeneral(p, parametres);
  if (maitre && maitre.valeur !== 'true') raisons.push(`« ${maitre.libelle} » est coupé`);
  for (const d of dependancesManquantes(p, etat)) raisons.push(DEPENDANCES[d].court);
  return raisons.length ? `Activé, mais sans effet pour l’instant : ${raisons.join(' et ')}.` : null;
}

export type Voyant = 'ok' | 'a-completer' | 'inactif';

export const LIBELLES_VOYANT: Record<Voyant, string> = {
  ok: 'En service',
  'a-completer': 'À compléter',
  inactif: 'Désactivé',
};

/** État d'une rubrique, en un mot :
 *  - « Désactivé » si son interrupteur général est coupé, ou si aucun de ses
 *    interrupteurs n'est allumé ;
 *  - « À compléter » si un interrupteur allumé reste sans effet ;
 *  - « En service » sinon. */
export function voyantRubrique(
  rubrique: ReadonlyArray<Lisible>,
  parametres: ReadonlyArray<Lisible>,
  etat: EtatDependances | null | undefined,
): Voyant {
  const maitre = rubrique.find(p => p.cle in INTERRUPTEURS_GENERAUX);
  if (maitre && maitre.valeur !== 'true') return 'inactif';
  if (rubrique.some(p => texteSansEffet(p, parametres, etat) !== null)) return 'a-completer';
  const interrupteurs = rubrique.filter(p => p.type === 'BOOLEEN');
  if (interrupteurs.length > 0 && interrupteurs.every(p => p.valeur !== 'true')) return 'inactif';
  return 'ok';
}

/** Dépendances absentes pour au moins un réglage de la rubrique, dans l'ordre
 *  de DEPENDANCES, sans doublon. */
export function dependancesRubrique(
  rubrique: ReadonlyArray<Pick<ParametreDetaille, 'dependances'>>,
  etat: EtatDependances | null | undefined,
): DependanceParametre[] {
  const absentes = new Set(rubrique.flatMap(p => dependancesManquantes(p, etat)));
  return (Object.keys(DEPENDANCES) as DependanceParametre[]).filter(d => absentes.has(d));
}

// --- Test de la clé d'IA -------------------------------------------------------

/** Codes d'échec rendus par POST /admin/settings/ia/tester : ceux d'ErreurIA,
 *  plus CLE_ABSENTE (aucune clé pour le fournisseur choisi). */
export type CodeErreurTestIA =
  | 'CLE_ABSENTE' | 'CLE_REFUSEE' | 'QUOTA' | 'INDISPONIBLE' | 'REQUETE' | 'DELAI' | 'REPONSE_VIDE' | 'JSON_INVALIDE';

const ECHECS_TEST_IA: Record<CodeErreurTestIA, string> = {
  CLE_ABSENTE: 'Aucune clé n’est enregistrée pour le fournisseur choisi : collez-la dans « Clé API du fournisseur ».',
  CLE_REFUSEE: 'Clé refusée par le fournisseur. Vérifiez qu’elle est complète et qu’elle correspond au fournisseur choisi.',
  QUOTA: 'Le fournisseur limite les appels (quota ou facturation). Réessayez plus tard ou vérifiez le compte du fournisseur.',
  INDISPONIBLE: 'Le fournisseur est indisponible pour l’instant. Réessayez dans quelques minutes.',
  REQUETE: 'Le fournisseur a refusé la requête. Le nom du modèle est peut-être inexact.',
  DELAI: 'Le fournisseur n’a pas répondu à temps. Réessayez.',
  REPONSE_VIDE: 'La clé est acceptée, mais le fournisseur a rendu une réponse vide.',
  JSON_INVALIDE: 'La clé est acceptée, mais la réponse du fournisseur est illisible.',
};

/** Échec du test dit en clair : un titre selon le code, et le message du
 *  serveur en détail quand il apporte quelque chose. */
export function messageTestIA(e: unknown): { titre: string; detail: string | null } {
  const statut = (e as { statusCode?: unknown } | null)?.statusCode;
  const code = String((e as { code?: unknown } | null)?.code ?? '');
  const message = e instanceof Error && e.message ? e.message : null;
  if (code in ECHECS_TEST_IA) {
    const titre = ECHECS_TEST_IA[code as CodeErreurTestIA];
    return { titre, detail: message && message !== titre ? message : null };
  }
  if (statut === 403 && !/MOT_DE_PASSE/i.test(code)) return { titre: 'Seul un administrateur peut tester la clé.', detail: null };
  if (statut === 401) return { titre: 'Votre session a expiré : reconnectez-vous puis recommencez.', detail: null };
  if (statut === 404 && message && /^Route non trouvée/i.test(message)) {
    return { titre: 'Le serveur ne propose pas encore ce test : la mise à jour du serveur doit d’abord être déployée.', detail: null };
  }
  return { titre: message ?? 'Test impossible pour l’instant. Réessayez dans un instant.', detail: null };
}

/** « 820 ms », « 1,4 s ». */
export function formaterLatence(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(Math.round(ms / 100) / 10).toLocaleString('fr-FR')} s`;
}

/** Résumé d'un test réussi. */
export function texteSuccesTestIA(r: { fournisseur: string; modele: string; latenceMs: number }): string {
  return `Clé valide : ${libelleOption('ia.fournisseur', r.fournisseur)}, modèle ${r.modele}, réponse en ${formaterLatence(r.latenceMs)}.`;
}
